/**
 * TimelineEditor.js — V2 bottom strip (video-editor style) + master timeline.
 *
 * ┌──────────────────────────────────────────────────────────────┐
 * │ 0:00  5:00  10:00 ... 60:00   ← master timeline (over viewport)
 * ├──────────────────────────────────────────────────────────────┤
 * │ ▬ drag handle (resize strip, min 128px)                      │
 * │ ┌──────────┬───────────────────────────────────────────────┐ │
 * │ │ Player 1 │ Movement  ─────────────────────────────────── │ │
 * │ │ Movement │ Action    ─────────────────────────────────── │ │
 * │ │ Action   │ Graphic   ─────────────────────────────────── │ │
 * │ │ Graphic  │                                                │ │
 * │ ├──────────┼───────────────────────────────────────────────┤ │
 * │ │ Player 2 │ ...                                            │ │
 * │ └──────────┴───────────────────────────────────────────────┘ │
 * └──────────────────────────────────────────────────────────────┘
 *
 * Each player's name sits to the LEFT of their own 3 tracks
 * (Movement / Action / Graphic) — one unit that scrolls vertically
 * together. The timeline fits the available width, so there is no
 * horizontal content scroll: the PLAYHEAD is the time scroller and
 * moves independently across the fixed view. Wheel over the tracks
 * scrubs through time; click/drag the master timeline to jump.
 */

import { useRef, useEffect, useCallback, useState, useLayoutEffect, useMemo, Fragment } from 'react';
import './TimelineEditor.css';
import { findSegment } from './data';
import { useI18n } from './i18n';
import PlayerActionIcon from './PlayerActionIcon';

const ICON_W = 30;              // track icon column width (px) — timeline starts after this
const LANE_H = 26;              // height of one track lane
const TRACKS_PER_PLAYER = 4;    // Movement | Action | Graphic | Mark
const MIN_STRIP_H = 128;

const TRACK_NAMES = ['Movement', 'Action', 'Graphic', 'Mark'];
// The 4th colour is the MARK lane, and it is deliberately ATTACH_COLOR (declared
// below): a mark IS an attach key — the marker is attached to an opponent at a
// bearing. Same data, same colour, so a rose diamond means "riding someone"
// wherever it appears in the strip.
const TRACK_COLORS = ['#38bdf8', '#f59e0b', '#a78bfa', '#fb7185'];

/** Default path-arrow colours per track type (Home blue · Away red · Ball gold). */
const TRACK_ARROW_DEFAULT = {
  home: '#3b82f6',
  away: '#ef4444',
  ball: '#f59e0b',
  cone: '#a78bfa',
  target: '#a78bfa',
  zone: '#a78bfa',
};

/** Track type prefix for a track id ('home:0' → 'home', 'ball:ball-1' → 'ball'). */
const trackTypeOf = (trackId) => {
  const prefix = String(trackId || '').split(':')[0];
  return TRACK_ARROW_DEFAULT[prefix] ? prefix : null;
};

/** Representative icons for each track (SVG, stroke-based to match the app style). */
const TRACK_ICONS = {
  Movement: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 9l-3 3 3 3" /><path d="M9 5l3-3 3 3" /><path d="M15 19l-3 3-3-3" />
      <path d="M19 9l3 3-3 3" /><path d="M2 12h20" /><path d="M12 2v20" />
    </svg>
  ),
  Action: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  Graphic: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  // MARK — the new lane. Drawn as the rule it encodes, not as a target: the big
  // ring is the marked player, the short line is the bearing, the filled dot is
  // the marker standing off at that bearing and gap. It is NOT the concentric
  // `target` glyph — that one means the Target item, and two icons that mean
  // different things must not look alike.
  Mark: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10.5" cy="14" r="7" /><path d="M10.5 14l6.8-6.8" />
      <circle cx="19" cy="5.5" r="2.6" fill="currentColor" stroke="none" />
    </svg>
  ),
  Visual: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="10.5" r="2.5" />
      <circle cx="8.5" cy="7.5" r="2.5" /><circle cx="6.5" cy="12.5" r="2.5" />
      <path d="M12 2a10 10 0 1 0 10 10" />
    </svg>
  ),
};

/** Small key glyph used on the per-player key buttons. */
/**
 * The key button's own glyph. It used to be an SVG "key" — but every refusal
 * that sends a coach to this button says ✚, and a control the prose cannot
 * name is a second thing to learn. One glyph: on the button and in the
 * sentence. It is also narrower than the SVG, which is what lets four of them
 * fit under a player's name.
 */
const KEY_ICON = (
  <span aria-hidden="true" style={{ fontSize: '10px', lineHeight: 1, fontWeight: 700 }}>✚</span>
);

/** Link icon — ball ATTACHMENT track (replaces the Action track for balls). */
const ICON_LINK = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 14a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" />
    <path d="M14 10a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" />
  </svg>
);

/** Item type icons — shown in the pinned item-tracks name cell. */
const ITEM_ICONS = {
  ball: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 2v7l4 3-2 7" /><path d="M12 9L5 12" /><path d="M12 9l7 4" />
      <path d="M5 12l7 3-3 6" /><path d="M16 12l-3 6 2 4" />
    </svg>
  ),
  cone: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L6 22h12L12 2z" /><path d="M8.5 14h7" />
    </svg>
  ),
  target: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  zone: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4" />
      <path d="M4 4h16v16H4z" opacity="0.35" />
    </svg>
  ),
};

/** Ball attachment keys use a distinct rose color (instead of the amber Action color). */
const ATTACH_COLOR = '#fb7185';

/** Player actions share one SVG per verb; direction is drawn into the glyph. */
export const ACTION_TYPES = [
  { value: 'pose_wave_left', label: 'Wave L', icon: <PlayerActionIcon action="wave" side="L" /> },
  { value: 'pose_wave_right', label: 'Wave R', icon: <PlayerActionIcon action="wave" side="R" /> },
  { value: 'pose_kick_left', label: 'Kick L', icon: <PlayerActionIcon action="kick" side="L" /> },
  { value: 'pose_kick_right', label: 'Kick R', icon: <PlayerActionIcon action="kick" side="R" /> },
  { value: 'pose_header_left', label: 'Head L', icon: <PlayerActionIcon action="header" side="L" /> },
  { value: 'pose_header_right', label: 'Head R', icon: <PlayerActionIcon action="header" side="R" /> },
  { value: 'pose_header_straight', label: 'Head C', icon: <PlayerActionIcon action="header" side="C" /> },
  { value: 'pose_shout', label: 'Shout', icon: <PlayerActionIcon action="shout" /> },
];

export function fmtTime(sec) {
  // Fixed-width MM:SS.CC — minutes, seconds, hundredths — 2 chars each,
  // so labels never change width (00:00.00 → 59:59.99).
  const s = Math.max(0, sec);
  const totalCs = Math.round(s * 100);
  const m = Math.floor(totalCs / 6000);
  const rem = totalCs % 6000;
  const ss = Math.floor(rem / 100);
  const cc = rem % 100;
  const mm = String(m).padStart(2, '0');
  return `${mm}:${String(ss).padStart(2, '0')}.${String(cc).padStart(2, '0')}`;
}

/** Fixed-width MM:SS for static ruler ticks (whole seconds, no centis). */
function fmtTick(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** KEY TIME GRID — all keyframes snap to 0.25s steps. Single source of truth
 *  for key creation, key dragging, playhead snapping and scrubbing. */
export const snapKeyTime = (t) => Math.round(t * 4) / 4;

/** Every key time in the open branch, sorted, de-duplicated. ONE collection, read
 *  by the ⌥+wheel hop and by the ruler's magnet alike — "the moments that actually
 *  have a key" must not mean two different things inside the same strip. */
export const collectKeyTimes = (keyframes) => {
  const times = new Set();
  for (const tr of keyframes || []) {
    for (const lane of ['position', 'action', 'attach', 'graphic', 'visual']) {
      for (const k of (tr[lane] || [])) times.add(k.time);
    }
  }
  return [...times].sort((a, b) => a - b);
};

/** How close (in pixels) a scrub has to come to a key before the playhead goes to
 *  that key instead. 8px is roughly a thumb's own radius: inside it the finger was
 *  aiming at the key; outside it the coach is scrubbing freely and must not be
 *  pulled anywhere. */
export const MAGNET_PX = 8;

/** Nearest key time within the magnet window of t at this zoom, else t. The list
 *  is sorted, so the walk stops at the first key past t — having compared it. */
export const magnetToKey = (t, keyTimes, pps) => {
  if (!Array.isArray(keyTimes) || !keyTimes.length || !(pps > 0)) return t;
  // Pixels, but never more than ONE grid step. Keys live on the 0.25s grid, so the
  // distance from a grid-snapped scrub to a key is always a multiple of 0.25s: a
  // window of 0.25 pulls exactly the neighbouring slot, and capping there means a
  // zoomed-out strip (where 8px is several grid steps) cannot swallow the slot the
  // coach was aiming at. Without the cap, zoomed out far enough, the magnet would
  // make it impossible to key a moment beside a key.
  const win = Math.min(MAGNET_PX / pps, 0.25);
  let best = null;
  let bestD = Infinity;
  for (const kt of keyTimes) {
    const d = Math.abs(kt - t);
    if (d < bestD) { bestD = d; best = kt; }
    if (kt > t) break;
  }
  return best !== null && bestD <= win ? best : t;
};

export default function TimelineEditor({
  players = [],        // [{ side, index, name, number, color }]
  selectedItem = null, // { type: 'ball'|'cone'|'target'|'zone', id } — pins its track GUI at the top
  selectedItemLabel = '', // human-friendly name for the pinned item block ('Ball 01')
  duration = 60,       // master timeline length in seconds
  onDurationChange = null, // (seconds) => void — user edited the timeline length
  onDurationDragEnd = null, // (newSeconds, oldSeconds) — an edge drag finished (toast hook)
  stripHeight = 200,
  onStripHeightChange = () => {},
  playheadTime = 0,    // seconds, current playback/scrub position
  speed = 1,           // playback rate — shown in the time bubble when ≠ 1× (state you can SEE)
  onSpeedCycle = null, // () => void — click the × chip to cycle 0.5→1→2→4 (mouse twin of +/−)
  onTimingMenu = null, // () => void — HOLD the ruler with a finger: this branch's timing menu
  isPlaying = false,   // current-branch playback running
  onPlayToggle = null, // () => void — play/pause only the open branch
  onScrub = null,      // (timeSeconds) => void — called when time changes
  keyframes = [],      // V2 keyframe tracks: [{ id, type, side?, index?, position: [{time, position}], graphic: [{time, value}] }]
  autoKeyOn = true,    // AUTO toggle state
  onAutoKeyToggle = null,
  onPlayerKey = null,      // (side, index) — add position key for a player at the playhead
  onActionPrompt = null,   // ({ side, index } | { trackId }) — open the central action modal
  onGraphicFlip = null,  // ({ side, index } | { trackId }) — FLIP the arrows lane
                         // at the playhead. No panel: the lane has two values and
                         // the app already knows which is showing, so the only
                         // live button was always the opposite one.
  onMarkPrompt = null,     // (trackId) — open the marking compass for a PLAYER (the ✚ on its MARK lane)
  onSelectItem = null,     // (item) => void — select an item on the field (clicking a player name)
  onItemPositionKey = null, // (trackId) — position key for an item track
  onBallAttachPrompt = null, // (trackId) — open the central attach-to-player prompt
  onKeyframeTimeChange = null, // (trackId, trackType, oldTime, newTime) — drag key to a new time
  onKeyframeRippleChange = null, // same signature — ⌥-drag: the rest of the timeline slides with the key
  onKeyframeDelete = null,    // (trackId, trackType, time) — double-click (mouse) / hold the diamond (touch)
  onKeyTap = null,            // (trackId, trackType, time) — tap attach/action dot → open its prompt at that instant
  onKeyframeClick = null,     // (trackId, time) — clicked a position keyframe (opens the height editor)
  keyframeEdit = null,        // { trackId, time } — position keyframe whose height editor is open (highlight)
  onTimelineDragStart = null, // () => void — user grabbed a keyframe to drag it (stop playback + open undo step)
  onTimelineDragEnd = null,   // () => void — keyframe drag finished (close the undo step)
  onSegmentClick = null,      // (trackId, from, to) — clicked a transition segment between keys
  segmentEdit = null,         // { trackId, from, to } — segment currently being edited (highlight)
  onZoneVisualSegClick = null, // (trackId, keyIndex) — clicked a zone visual window
  onZoneVisualKey = null,     // (trackId) — add a zone visual key at the playhead
  zoneVisualEdit = null,      // { trackId, keyIndex } — zone visual window being edited
  onGraphicSegClick = null,   // (trackId, start, end) — clicked an arrow window between graphic keys
  graphicEdit = null,         // { trackId, start, end } — arrow window currently being edited (highlight)
  onBranchBack = null,        // V3: () => void — leave this branch's timeline for the scenario tree
  branchName = '',            // V3: name of the branch being edited (shown next to BACK)
  branchId = null,            // V3: id of the branch being edited (double-click / hold to rename)
  onRenameBranch = null,      // V3: (branchId, name) => void — commit an inline rename (no-op-guarded + undoable in App)
  lockedTime = null,          // V3: keys at this time are inherited/read-only (0 on sub-branches, null on the root)
}) {
  const { t } = useI18n();
  const lanesRef = useRef(null);
  const [lanesWidth, setLanesWidth] = useState(0);
  // Inline rename of the branch shown in the header (double-click the name).
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  useEffect(() => { setRenaming(false); }, [branchId]);
  // Editable timeline length (seconds) — local text while editing, synced from the prop.
  const [durText, setDurText] = useState(String(duration));
  useEffect(() => { setDurText(String(duration)); }, [duration]);

  // ── V3: INHERITED (READ-ONLY) KEYS ──────────────────────────────────────
  // On a sub-branch the first key is the previous branch's last key: it is what
  // makes the branch continue from its parent, so it cannot be dragged, moved,
  // deleted or edited. On the root branch `lockedTime` is null and everything
  // is editable, exactly like V2.
  const isLockedKey = (time) => lockedTime !== null && lockedTime !== undefined
    && Math.abs(time - lockedTime) < 0.001;
  // The lane's internal name is English (it keys TRACK_ICONS and the data
  // model); the tooltip a finger hovers over is not.
  const LANE_NAME_KEYS = {
    Movement: 'tl.tnMovement', Action: 'tl.tnAction', Graphic: 'tl.tnGraphic',
    Mark: 'tl.tnMark', Visual: 'tl.tnVisual', Attachment: 'tl.tnAttachment',
  };
  const laneName = (tn) => t(LANE_NAME_KEYS[tn] || 'tl.tnMovement');

  /** Shared drag/dbl-click handlers for a key — disabled when the key is inherited. */
  const keyHandlers = (time, onDown, onDelete, shape = 'diamond') => (
    isLockedKey(time)
      ? {}
      : {
          // The painted marker stays 8-10px (lanes are dense), but on a phone a
          // thumb cannot hit that. 'v3-key' is the hook App.css uses to grow an
          // invisible hit box around it via a pseudo-element — same pixels,
          // three times the target, no lane re-layout.
          //
          // 'v3-key-diamond' carries the 45° turn itself, and App.css turns the
          // hit box back by -45°: a pseudo-element inherits its element's
          // transform, so a box drawn square to the lane would otherwise be
          // tilted 38px across a 26px lane — stealing the tap from the lane
          // above or below, the one thing its own comment promises it never does.
          className: shape === 'diamond' ? 'v3-key v3-key-diamond' : 'v3-key',
          onPointerDown: onDown,
          onDoubleClick: (e) => { e.stopPropagation(); onDelete && onDelete(); },
        }
  );
  /**
   * Resolve an attach key value ('home:3' / 'ball:ball-01' / 'none') to a
   * readable label. ONE definition, in the component body, because both rows
   * need it: the item row's attach tooltip and the player row's new MARK lane.
   * Two copies of a name are two names that drift.
   *
   * 'Detached' and 'Ball' used to be English sitting inside a tooltip on a
   * Portuguese interface — the carrier's name now comes from the dictionary.
   */
  const attachLabel = (v) => {
    if (!v || v === 'none') return t('tl.detached');
    if (v.startsWith('ball:')) return `${t('itm.ball')} ${v.slice(5).replace(/^ball-/, '')}`;
    // A zone, cone or target carrier: the item's OWN menu word. Any of the three
    // can carry now — the sampler walks to whatever track a drop or a pick
    // named — and a raw 'cone:c7' in a tooltip on a Portuguese interface is a
    // data value speaking to a person.
    const kind = v.split(':')[0];
    if (['zone', 'cone', 'target'].includes(kind)) {
      return t({ zone: 'itm.zone', cone: 'itm.cone', target: 'itm.target' }[kind]);
    }
    const [sid, idx] = v.split(':');
    const pl = players.find((p) => p.side === sid && p.index === parseInt(idx, 10));
    return pl ? `${sid === 'home' ? 'H' : 'A'}#${pl.index + 1} ${pl.name || ''}` : v;
  };
  /** Marker drawn on an inherited key so it reads as locked. */
  const lockBadge = (time) => isLockedKey(time) ? (
    <span key={`lock${time}`} style={{
      position: 'absolute', top: '50%', left: time * pps - 12, marginTop: -6,
      fontSize: 9, lineHeight: '12px', width: 12, height: 12, textAlign: 'center',
      background: '#f59e0b', color: '#1f2937', borderRadius: 3, zIndex: 3,
      fontWeight: 900, pointerEvents: 'none',
    }}>🔒</span>
  ) : null;

  /**
   * The MARK lane's diamonds, drawn ONE way — in the squad row and in the
   * pinned block alike. Filled rose = marking someone; hollow = the mark was
   * released at that second (the gap the release key leaves is its whole
   * point, so it shows). Tapping one opens the same compass the ✚ writes, at
   * that instant. Two copies of this markup is how a mark ends up looking
   * different depending on which half of the strip you happen to be reading.
   */
  const markLane = (trackId, keys) => keys.map(k => (
    <div
      key={`m${k.time}`}
      {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'attach', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'attach', k.time))}
      title={isLockedKey(k.time) ? t('branch.lockedKey')
        : ((k.value && k.value !== 'none'
          ? t('tl.markKey', { who: attachLabel(k.value), t: fmtTime(k.time) })
          : t('tl.markKeyOff', { t: fmtTime(k.time) })) + t('tl.keyHoldDelete'))}
      style={{
        position: 'absolute', top: '50%', left: k.time * pps - 4,
        width: 8, height: 8, marginTop: -4,
        background: k.value && k.value !== 'none' ? TRACK_COLORS[3] : 'transparent',
        border: `1.5px solid ${TRACK_COLORS[3]}`,
        borderRadius: 1,
        cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize', zIndex: 2, touchAction: 'none',
      }}
    />
  ));

  /**
   * The ATTACHMENT lane's diamonds, drawn ONE way. For a ball or a zone this is
   * the middle lane; for a cone or a target it is the 4th lane, which appears
   * only while the item is actually carried. Same rose, same hold-to-delete,
   * same carrier name from attachLabel — two copies of this markup is how a
   * follow ends up looking different depending on which shape happens to be
   * riding someone.
   */
  const attachLane = (trackId, keys) => keys.map(k => (
    <div
      key={`a${k.time}`}
      {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'attach', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'attach', k.time))}
      title={isLockedKey(k.time) ? t('branch.lockedKey')
        : (t('tl.midAttach', { what: attachLabel(k.value), t: fmtTime(k.time) }) + t('tl.keyHoldDelete'))}
      style={{
        position: 'absolute', top: '50%', left: k.time * pps - 4,
        width: 8, height: 8, marginTop: -4, background: ATTACH_COLOR,
        border: '1px solid #0b1120', borderRadius: 1,
        cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize', zIndex: 2, touchAction: 'none',
      }}
    />
  ));

  // ── Measure the lanes area so the timeline fits its width ──
  useLayoutEffect(() => {
    const el = lanesRef.current;
    if (!el) return;
    const measure = () => setLanesWidth(el.clientWidth);
    measure();
    let queued = false;
    const ro = new ResizeObserver(() => {
      // One measure per frame, outside the observer's delivery pass — measuring
      // here and writing layout back is what trips "ResizeObserver loop
      // completed with undelivered notifications".
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; measure(); });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Recalculate the name column after rotation; desktop retains its 172px width.
  const NAME_W = Math.max(120, Math.min(172, Math.round((lanesWidth || window.innerWidth) * 0.34)));
  const contentW = Math.max((lanesWidth || 800) - NAME_W - ICON_W, 1);
  const pps = contentW / Math.max(duration, 1); // pixels per second (fit-to-width)
  const TIME_X = NAME_W + ICON_W;               // time axis starts after the name + icon columns

  // SCROLL THE SELECTED PLAYER'S WHOLE ROW INTO VIEW.
  // A player row is four lanes tall now (Movement · Action · Graphic · MARK),
  // and eleven of them will never fit in a phone-height strip. Before the fourth
  // lane existed the row you had selected always fit; now the bottom one — the
  // MARK lane, the one people came here to see — routinely sat below the fold
  // and read as "missing". Selecting a row is a request to see that row, so the
  // strip honours it, and only moves when the row is genuinely out of sight.
  useEffect(() => {
    const box = lanesRef.current;
    if (!box || !selectedItem || selectedItem.type !== 'player') return;
    const row = box.querySelector(`[data-player-row="${selectedItem.side}:${selectedItem.index}"]`);
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < box.scrollTop) box.scrollTop = top;
    else if (bottom > box.scrollTop + box.clientHeight) box.scrollTop = bottom - box.clientHeight;
    // Primitives, deliberately. The parent hands down a fresh selectedItem
    // object on most renders; depending on it would re-run this every time and
    // fight the coach's own thumb-scroll. Only a genuinely different player —
    // or a row list that changed size — should move the strip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.type, selectedItem?.side, selectedItem?.index, players.length]);

  // ── Drag a keyframe marker along the time axis (live update) ──
  // Uses window-level pointer listeners so the drag keeps working even though
  // the marker element re-mounts on every time change (it is keyed by time).
  // ── The hold, in ONE place ──
  // Three controls in this strip answer to a finger that sits still: the ruler
  // (this branch's timing menu), every keyframe diamond (delete the key) and
  // the branch name (rename it). They must mean the SAME thing, so the ladder
  // is written once — one beat, one slop, one timer — and a gesture hands its
  // pointer samples to it. Two literals would have been two chances to
  // disagree, and they already did: the ruler measured its slop from the
  // landing point while the keys measured it per sample.
  const HOLD_MS = 550;
  const HOLD_SLOP = 6;   // px of travel that still counts as "held, not slid"
  const refHold = useRef(null);   // the live timer
  const holdSeq = useRef(0);      // which gesture owns it
  // A slide cancels the hold IMMEDIATELY, not at the beat: a drag must never
  // turn into a delete (or a rename). The threshold is measured per SAMPLE, not
  // from where the finger landed — an 8 px diamond under a thumb reports a few
  // pixels of jitter, while a real drag moves far more than HOLD_SLOP in one
  // step. Forgiving to a still finger, instant for a moving one.
  const holdCancel = useCallback(() => {
    holdSeq.current += 1;          // this hold is dead, whoever set its timer
    if (refHold.current) { clearTimeout(refHold.current); refHold.current = null; }
  }, []);
  // Arm a hold. Returns the hooks the owning gesture must feed: onSample for
  // every pointermove x, end for the lift. A non-touch pointer never arms one,
  // so the mouse keeps its own verbs (double-click) untouched.
  const beginHold = useCallback((e, onFire) => {
    if (e.pointerType !== 'touch') return null;
    holdCancel();
    const seq = ++holdSeq.current;
    refHold.current = setTimeout(() => {
      if (seq !== holdSeq.current) return;
      refHold.current = null;
      onFire();
    }, HOLD_MS);
    let lastX = e.clientX;
    return {
      onSample: (x) => {
        if (Math.abs(x - lastX) > HOLD_SLOP) holdCancel();
        lastX = x;
      },
      end: holdCancel,
    };
  }, [holdCancel]);
  // True while/after a keyframe drag — distinguishes a drag-move from a tap.
  const keyDragMovedRef = useRef(false);
  // Every grab attaches its own move/up pair to window, so the previous grab's
  // pair has to be torn down first: left attached, an old onMove keeps running
  // alongside the new one and drags the EARLIER key too, and the count of live
  // listeners grows one pair per drag.
  const detachKeyDrag = useRef(null);
  const startKeyDrag = (e, trackId, trackType, time) => {
    e.stopPropagation();
    e.preventDefault();
    if (detachKeyDrag.current) { detachKeyDrag.current(); detachKeyDrag.current = null; }
    keyDragMovedRef.current = false;
    if (onTimelineDragStart) onTimelineDragStart();
    const area = e.currentTarget.parentElement; // lane time area (stable across re-renders)
    const isPrimary = e.button === 0;
    // ⌥ held at grab time = RIPPLE drag: the key moves AND everything after it
    // slides by the same delta (the mouse twin of [ / ]). Captured once — the
    // modifier must not flip mid-drag.
    const ripple = e.altKey && !!onKeyframeRippleChange;
    let curTime = time;
    // HOLD A KEY with a finger = delete it. A keyframe has always been
    // double-click-to-delete, and a phone has no double-click: the strip's own
    // diamonds were the ONE verb in the whole app a thumb could not reach. (The
    // attach/MARK lanes grew a ✕ inside their prompt, but that only covers a
    // key you already opened — a mistake you want gone takes two taps and a
    // panel.)
    //
    // The key element is keyed by its time and re-mounts the instant a drag
    // moves it, so its identity is captured at grab time: that is the key the
    // finger actually touched.
    const grab = { trackId, trackType, time };
    const hold = isPrimary && onKeyframeDelete && !isLockedKey(time)
      ? beginHold(e, () => {
          // Belt to the slide-cancel inside beginHold: if the timer fires a
          // hair after the finger went up, keyDragMovedRef says so. Never
          // delete a key the coach was still dragging.
          if (keyDragMovedRef.current) return;
          onKeyframeDelete(grab.trackId, grab.trackType, grab.time);
        })
      : null;
    const onMove = (ev) => {
      const mx = ev.clientX;
      if (hold) hold.onSample(mx);
      const rect = area.getBoundingClientRect();
      // THE SAME MAGNET the ruler uses, now on the drag that moves a key. Lining
      // two keys up — a run starting at the instant its action fires — means
      // landing on one 0.25s slot out of hundreds, with a thumb. The dragged key's
      // own old time is in the list and pulling to it is a no-op: the key stays
      // where the finger grabbed it until the finger moves past that slot.
      const t = magnetToKey(
        snapKeyTime(Math.max(0, Math.min(duration, (mx - rect.left) / pps))),
        keyTimes, pps);
      if (t !== curTime) {
        const fn = ripple ? onKeyframeRippleChange : onKeyframeTimeChange;
        if (fn) {
          keyDragMovedRef.current = true;
          const accepted = fn(trackId, trackType, curTime, t);
          if (accepted !== false) curTime = t;
        }
      }
    };
    const onUp = () => {
      if (hold) hold.end();           // a lifted finger is not holding anything
      detach();
      if (onTimelineDragEnd) onTimelineDragEnd();
      // Tap (no drag) on a POSITION key opens its height editor. Done here on
      // pointerup — NOT via onClick — because preventDefault() on pointerdown
      // suppresses the click event in some browsers (Safari/iOS).
      if (isPrimary && !keyDragMovedRef.current && trackType === 'position' && onKeyframeClick) {
        onKeyframeClick(trackId, curTime);
      } else if (isPrimary && !keyDragMovedRef.current
          && (trackType === 'attach' || trackType === 'action' || trackType === 'graphic' || trackType === 'visual')
          && onKeyTap) {
        // Tap an ATTACH dot → re-pick the carrier at this instant;
        // ACTION → re-pick the action; GRAPHIC → FLIP it, because that lane has
        // two values and the state under the finger is already known (App
        // writes the opposite through the same verb the menu's ➜ uses);
        // VISUAL → that window's shape editor. All act AT THE KEY.
        onKeyTap(trackId, trackType, curTime);
      }
      keyDragMovedRef.current = false;
    };
    const detach = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (detachKeyDrag.current === detach) detachKeyDrag.current = null;
    };
    detachKeyDrag.current = detach;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // HOLD an empty stretch of a lane = write that lane's key AT THE INSTANT YOU
  // TOUCHED. Adding a key used to take two gestures, on the worst target in the
  // app: scrub the ruler to the second you want (a 1-px line under a thumb),
  // then tap the ✚ in the name column — which keys at the PLAYHEAD, so the
  // number you aimed at and the number you got were different things. Pressing
  // where the key belongs is the same verb, spatially: one gesture, and the
  // instant IS the place the finger landed. A slide is untouched (it scrolls or
  // scrubs, as before), a press on an existing diamond still drags it, and the
  // mouse keeps its ✚.
  const startLaneHold = (e, trackId, laneType) => {
    if (!onKeyTap || !laneType || e.button !== 0) return;
    const area = e.currentTarget;
    const rect = area.getBoundingClientRect();
    const at = snapKeyTime(Math.max(0, Math.min(duration, (e.clientX - rect.left) / pps)));
    const hold = beginHold(e, () => onKeyTap(trackId, laneType, at));
    if (!hold) return;
    const onMove = (ev) => hold.onSample(ev.clientX);
    const onUp = () => {
      hold.end();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  // Which lane a press belongs to. The zone's VISUAL lane is the one lane whose
  // ✚ edits a WINDOW rather than keys a moment, so holding it offers nothing —
  // and its tooltip says nothing, rather than promising a verb it lacks.
  const PLAYER_LANE_TYPES = ['position', 'action', 'graphic', 'attach'];

  // ── Wheel over the MASTER TIMELINE bar = scrub through time ──
  // (Over the track lanes the wheel natively scrolls the player list instead.)
  // ALT+wheel = hop to the next/previous KEY — the mouse twin of , / .:
  // jump between moments, not between pixels.
  const rulerRef = useRef(null);
  // The moments that exist, computed once per keyframe set: the hop below and the
  // ruler's magnet both read THIS list.
  const keyTimes = useMemo(() => collectKeyTimes(keyframes), [keyframes]);
  const handleWheel = useCallback((e) => {
    if (!onScrub) return;
    e.preventDefault();
    if (e.altKey) {
      const sorted = keyTimes;
      const dir = e.deltaY > 0 ? 1 : -1;
      const eps = 1e-9;
      const hop = dir > 0
        ? sorted.find(t => t > playheadTime + eps)
        : sorted.slice().reverse().find(t => t < playheadTime - eps);
      if (hop !== undefined) onScrub(hop);
      return;
    }
    const px = e.deltaMode === 1
      ? e.deltaY * 16
      : (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY);
    onScrub(Math.max(0, Math.min(duration, snapKeyTime(playheadTime + px / pps))));
  }, [onScrub, playheadTime, duration, pps, keyTimes]);

  useEffect(() => {
    const el = rulerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── Resize: drag the strip's top edge ──
  const startResize = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = stripHeight;
    const onMove = (ev) => {
      const delta = startY - ev.clientY; // drag up → taller
      onStripHeightChange(Math.max(MIN_STRIP_H, startH + delta));
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [stripHeight, onStripHeightChange]);

  // ── Scrub on the master timeline (click or drag) ──
  const scrubFromEvent = useCallback((clientX, rulerEl) => {
    if (!onScrub) return;
    const rect = rulerEl.getBoundingClientRect();
    // Grid first (the 0.25s key grid), then the magnet: within a thumb's radius of
    // a moment that HAS a key, the playhead lands on that key. A mouse gets the
    // same rule — there is no free scrub to lose, only pixels of aim to gain — and
    // outside the window nothing changes at all.
    const t = magnetToKey(
      snapKeyTime((clientX - rect.left - TIME_X) / pps), keyTimes, pps);
    onScrub(Math.max(0, Math.min(duration, t)));
  }, [pps, duration, onScrub, TIME_X, keyTimes]);

  const startScrub = useCallback((e) => {
    if (!onScrub) return;
    e.preventDefault();
    const rulerEl = e.currentTarget;
    scrubFromEvent(e.clientX, rulerEl);
    // HOLD the ruler with a finger = this branch's TIMING menu. Scrub still
    // happens on press-down (that is the ruler's job), but a finger that holds
    // still is asking for more than a scrub — the SAME ladder the keyframe
    // diamonds and the branch name use, not a second copy of it.
    const hold = onTimingMenu ? beginHold(e, onTimingMenu) : null;
    const onMove = (ev) => {
      if (hold) hold.onSample(ev.clientX);
      scrubFromEvent(ev.clientX, rulerEl);
    };
    const onUp = () => {
      if (hold) hold.end();
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [scrubFromEvent, onScrub, onTimingMenu, beginHold]);

  // HOLD the branch's NAME = rename it. Renaming has always been
  // double-click, and the tooltip promised "tap twice" — a gesture iOS never
  // delivers as a dblclick, so on a phone the promise was a lie and a branch
  // could not be renamed from the strip at all. Same ladder as the ruler and
  // the diamonds, one more control answering to one more gesture.
  const startNameHold = (e) => {
    // The name is a control, not timeline: pressing it must not also scrub the
    // playhead to wherever the header happens to sit.
    e.stopPropagation();
    if (!onRenameBranch || !branchId) return;
    const hold = beginHold(e, () => { setRenameDraft(branchName); setRenaming(true); });
    if (!hold) return;
    const onMove = (ev) => hold.onSample(ev.clientX);
    const onUp = () => {
      hold.end();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── Ruler ticks ──
  // Label every 5s when there's room; on narrow screens (portrait phones)
  // the labels would overlap (each "0:00" ≈ 20px wide), so space them out.
  // Tick lines stay at 1s/5s regardless — only the TEXT labels thin out.
  const labelEvery = pps < 5 ? 15 : pps < 8 ? 10 : 5;
  const ticks = [];
  for (let s = 0; s <= duration; s++) {
    const major = s % 5 === 0;
    ticks.push(
      <div key={s} style={{
        position: 'absolute', left: TIME_X + s * pps, top: 0, bottom: 0, width: 1,
        background: major ? 'rgba(148,163,184,0.6)' : 'rgba(148,163,184,0.18)',
      }} />
    );
    if (major && s % labelEvery === 0) {
      ticks.push(
        <span key={`l${s}`} style={{
          position: 'absolute', left: TIME_X + s * pps + 3, top: 2,
          color: '#94a3b8', fontSize: '9px', fontWeight: '600', whiteSpace: 'nowrap',
        }}>
          {fmtTick(s)}
        </span>
      );
    }
  }

  // ── Clickable transition segment between two movement keys (position lane) ──
  const segmentBars = (track, trackId) => {
    const keys = track?.position || [];
    return keys.map((k, ki) => {
      if (ki === keys.length - 1) return null;
      const k1 = keys[ki + 1];
      if (k1.time <= k.time) return null;
      const seg = findSegment(track, k.time, k1.time);
      const active = !!segmentEdit && segmentEdit.trackId === trackId
        && segmentEdit.from === k.time && segmentEdit.to === k1.time;
      const styleName = seg.style === 'curveLeft' ? 'Curve L' : seg.style === 'curveRight' ? 'Curve R' : 'Straight';
      const archNote = seg.arch > 0 ? ` · arch ${seg.arch.toFixed(1)}m` : '';
      const w = (k1.time - k.time) * pps;
      return (
        <button
          key={`seg${k.time}-${k1.time}`}
          type="button"
          onClick={() => onSegmentClick && onSegmentClick(trackId, k.time, k1.time)}
          title={t('tl.segBar', { a: fmtTime(k.time), b: fmtTime(k1.time), style: styleName, arch: archNote })}
          style={{
            position: 'absolute', top: 2, bottom: 2, left: k.time * pps, width: Math.max(4, w - 1),
            background: active ? 'rgba(167,139,250,0.4)' : 'rgba(167,139,250,0.12)',
            border: `1px solid ${active ? '#a78bfa' : 'rgba(167,139,250,0.35)'}`,
            borderRadius: 4, cursor: 'pointer', zIndex: 1, padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          }}
        >
          <svg width="12" height="8" viewBox="0 0 12 8" style={{ flexShrink: 0, opacity: active ? 1 : 0.75 }}>
            {seg.style === 'straight' && <path d="M1 4 H11" stroke="#a78bfa" strokeWidth="1.5" fill="none" />}
            {seg.style === 'curveLeft' && <path d="M1 4 Q 6 -1 11 4" stroke="#a78bfa" strokeWidth="1.5" fill="none" />}
            {seg.style === 'curveRight' && <path d="M1 4 Q 6 9 11 4" stroke="#a78bfa" strokeWidth="1.5" fill="none" />}
          </svg>
        </button>
      );
    });
  };

  // ── Clickable graphic window bars (graphic lane) ──
  // Between each ON key and the next key (or the timeline end) the path arrow
  // is drawn — clicking the bar opens the arrow-colour editor for that window.
  // The bar previews the window's arrow colour (override or track default).
  const graphicBars = (track, trackId) => {
    const gk = (track?.graphic || []).slice().sort((a, b) => a.time - b.time);
    const bars = [];
    for (let i = 0; i < gk.length; i++) {
      if (gk[i].value !== 'on') continue;
      const start = gk[i].time;
      const end = i + 1 < gk.length ? gk[i + 1].time : duration;
      const w = (end - start) * pps;
      if (w < 4) continue;
      const color = gk[i].color || TRACK_ARROW_DEFAULT[trackTypeOf(trackId)] || '#a78bfa';
      const active = !!graphicEdit && graphicEdit.trackId === trackId
        && graphicEdit.start === start && graphicEdit.end === end;
      bars.push(
        <button
          key={`gwin${start}-${end}`}
          type="button"
          onClick={() => onGraphicSegClick && onGraphicSegClick(trackId, start, end)}
          title={t('tl.arrowBar', { a: fmtTime(start), b: fmtTime(end) })}
          style={{
            position: 'absolute', top: 2, bottom: 2, left: start * pps, width: Math.max(4, w - 1),
            background: active ? `${color}55` : `${color}22`,
            border: `1px dashed ${active ? color : `${color}66`}`,
            borderRadius: 4, cursor: 'pointer', zIndex: 1, padding: 0,
          }}
        />
      );
    }
    return bars;
  };

  // ── Clickable zone-visual window bars (visual lane, zone tracks only) ──
  // Between each visual key and the next one (or the timeline end) the zone's
  // extra properties (radius / shape points / vertex attachments) apply —
  // clicking the bar opens the zone-visual editor for that window.
  const visualBars = (track, trackId) => {
    const vk = (track?.visual || []).slice().sort((a, b) => a.time - b.time);
    const bars = [];
    for (let i = 0; i < vk.length; i++) {
      const start = vk[i].time;
      const end = i + 1 < vk.length ? vk[i + 1].time : duration;
      const w = (end - start) * pps;
      if (w < 4) continue;
      const color = '#a78bfa';
      const active = !!zoneVisualEdit && zoneVisualEdit.trackId === trackId && zoneVisualEdit.keyIndex === i;
      bars.push(
        <button
          key={`vwin${start}-${end}`}
          type="button"
          onClick={() => onZoneVisualSegClick && onZoneVisualSegClick(trackId, i)}
          title={t('tl.zoneVisBar', { a: fmtTime(start), b: fmtTime(end) })}
          style={{
            position: 'absolute', top: 2, bottom: 2, left: start * pps, width: Math.max(4, w - 1),
            background: active ? `${color}55` : `${color}22`,
            border: `1px dashed ${active ? color : `${color}66`}`,
            borderRadius: 4, cursor: 'pointer', zIndex: 1, padding: 0,
          }}
        />
      );
    }
    return bars;
  };

  // ── Player blocks: name cell left of the 3 track lanes ──
  const blocks = players.map((p, pi) => {
    // Position ("movement") track for this player — keyframes on the 0.25s grid
    const trackId = `${p.side}:${p.index}`;
    const track = keyframes.find(k => k.id === trackId);
    const posKeys = track?.position || [];
    const actionKeys = track?.action || [];
    const graphicKeys = track?.graphic || [];
    // A PLAYER's attach lane is the MARK lane: the mark verb is the only thing
    // that ever writes it for a player (the 📎 Attach entry is offered to cones,
    // targets, zones and the ball, never to a player). So the same lane that
    // carries a ball's carrier carries a marker's opponent — and the strip can
    // show it as its own row without a second copy of the data.
    const markKeys = track?.attach || [];
    // Selected on the field → highlight the name row; clicking the name
    // selects (or deselects) the player on the field.
    const isSel = selectedItem?.type === 'player'
      && selectedItem?.side === p.side
      && selectedItem?.index === p.index;
    return (
    <div key={`${p.side}-${p.index}`} data-player-row={`${p.side}:${p.index}`} style={{
      display: 'flex', height: TRACKS_PER_PLAYER * LANE_H,
      borderBottom: '1px solid rgba(255,255,255,0.10)',
      background: pi % 2 === 1 ? 'rgba(255,255,255,0.02)' : 'transparent',
    }}>
      {/* Name cell — player name + 3 key buttons, left of this player's tracks */}
      <div style={{
        width: NAME_W, flexShrink: 0, background: 'rgba(15,23,42,0.9)',
        borderRight: '1px solid #334155',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        gap: '3px', padding: '0 6px',
      }}>
        <div
          onClick={(e) => { e.stopPropagation(); onSelectItem && onSelectItem(isSel ? null : { type: 'player', side: p.side, index: p.index }); }}
          title={isSel ? t('tl.rowSelected') : t('tl.rowUnselected')}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0,
            borderRadius: '5px', padding: '2px 3px', margin: '-2px -3px',
            cursor: 'pointer', touchAction: 'manipulation', userSelect: 'none',
            background: isSel ? 'rgba(59,130,246,0.22)' : 'transparent',
            boxShadow: isSel ? 'inset 2px 0 0 #3b82f6' : 'none',
          }}>
          <span style={{
            width: '18px', height: '18px', borderRadius: '3px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            // Side-identity fallback (home blue / away red) — matches App's timelinePlayers
            background: p.color || (p.side === 'home' ? '#2563eb' : '#dc2626'),
            color: '#fff', fontSize: '9px', fontWeight: '800',
          }}>
            {p.number || pi + 1}
          </span>
          <span style={{
            color: '#e2e8f0', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
          }}>
            {p.name || `Player ${pi + 1}`}
          </span>
          <span style={{ color: '#64748b', fontSize: '9px', fontWeight: '600', flexShrink: 0 }}>
            {p.side === 'home' ? 'H' : 'A'}
          </span>
        </div>
        {/* Key buttons — one per track, under the player name (key glyph + track icon) */}
        <div className="v3-keyrow" style={{ display: 'flex', gap: '2px', position: 'relative' }}>
          <button
            type="button"
            onClick={() => onPlayerKey && onPlayerKey(p.side, p.index)}
            title={t('tl.keyMovementPlayer')}
            style={{ ...keyBtnStyle, color: TRACK_COLORS[0] }}
          >{KEY_ICON}{TRACK_ICONS['Movement']}</button>
          <button
            type="button"
            onClick={() => onActionPrompt && onActionPrompt({ side: p.side, index: p.index })}
            title={t('tl.keyAction')}
            style={{ ...keyBtnStyle, color: '#e2e8f0' }}
          >{KEY_ICON}{TRACK_ICONS['Action']}</button>
          <button
            type="button"
            onClick={() => onGraphicFlip && onGraphicFlip({ side: p.side, index: p.index })}
            title={t('tl.keyGraphic')}
            style={{ ...keyBtnStyle, color: '#e2e8f0' }}
          >{KEY_ICON}{TRACK_ICONS['Graphic']}</button>
          {/* ✚ MARK — the 4th slot. It opens the same compass the 🎯 entry in
              the player's menu opens, from the row the mark key lands in. */}
          <button
            type="button"
            onClick={() => onMarkPrompt && onMarkPrompt(`${p.side}:${p.index}`)}
            title={t('tl.keyMark')}
            style={{ ...keyBtnStyle, color: TRACK_COLORS[3] }}
          >{KEY_ICON}{TRACK_ICONS['Mark']}</button>
        </div>
      </div>
      {/* The 4 track lanes — stacked vertically, each with its icon, to the right of the name.
          The time area begins AFTER the icon column, so the playhead never overlaps the icons. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
        {TRACK_NAMES.map((tn, ti) => (
          <div key={tn} style={{
            height: LANE_H, borderBottom: ti < TRACK_NAMES.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
            display: 'flex', alignItems: 'center',
          }}>
            {/* Track icon — compact representative icon (MOVEMENT / ACTION / GRAPHIC / MARK) */}
            <span title={laneName(tn) + t('tl.laneHold')} style={{
              width: ICON_W, flexShrink: 0, color: TRACK_COLORS[ti],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              userSelect: 'none',
            }}>
              {TRACK_ICONS[tn]}
            </span>
            {/* Time area for this lane — keyframe markers (drag to move, hold to delete).
                Pressing an EMPTY stretch and holding writes this lane's key there. */}
            <div
              data-testid="lane-area"
              onPointerDown={(e) => startLaneHold(e, trackId, PLAYER_LANE_TYPES[ti])}
              style={{ flex: 1, height: '100%', minWidth: 0, position: 'relative' }}
            >
              {/* Movement lane: transition segment (clickable) + position keys — blue diamonds.
                  Click = height editor (onKeyframeClick), drag = move in time,
                  double-click = delete. Highlighted while its editor is open. */}
              {ti === 0 && segmentBars(track, trackId)}
              {ti === 0 && posKeys.map(k => {
                const kfActive = !!keyframeEdit && keyframeEdit.trackId === trackId && keyframeEdit.time === k.time;
                return (
                  <Fragment key={`p${k.time}`}>
                    <div
                      {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'position', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'position', k.time))}
                      title={isLockedKey(k.time) ? t('branch.lockedKey') : t('tl.posKey', { t: fmtTime(k.time) }) + t('tl.keyHoldDelete')}
                      style={{
                        position: 'absolute', top: '50%', left: k.time * pps - 4,
                        width: 8, height: 8, marginTop: -4,
                        background: kfActive ? '#f59e0b' : TRACK_COLORS[0], border: `1px solid ${kfActive ? '#b45309' : '#0b1120'}`,
                        borderRadius: 1,
                        cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize',
                        zIndex: 2, touchAction: 'none',
                      }}
                    />
                    {lockBadge(k.time)}
                  </Fragment>
                );
              })}
              {/* Action lane: action keys — amber diamonds */}
              {ti === 1 && actionKeys.map(k => (
                <div
                  key={`a${k.time}`}
                  {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'action', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'action', k.time))}
                  title={isLockedKey(k.time) ? t('branch.lockedKey') : t('tl.actionKey', { what: ACTION_TYPES.find(a => a.value === k.value)?.label || k.value, t: fmtTime(k.time) }) + t('tl.keyHoldDelete')}
                  style={{
                    position: 'absolute', top: '50%', left: k.time * pps - 4,
                    width: 8, height: 8, marginTop: -4,
                    background: TRACK_COLORS[1], border: '1px solid #0b1120',
                    borderRadius: 1,
                    cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize',
                    zIndex: 2, touchAction: 'none',
                  }}
                />
              ))}
              {/* Graphic lane: on/off keys — purple squares (filled = on, hollow = off) */}
              {ti === 2 && graphicBars(track, trackId)}
              {ti === 2 && graphicKeys.map(k => (
                <div
                  key={`g${k.time}`}
                  {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'graphic', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'graphic', k.time), 'square')}
                  title={isLockedKey(k.time) ? t('branch.lockedKey') : t('tl.graphicKey', { value: k.value === 'on' ? t('tl.on') : t('tl.off'), t: fmtTime(k.time) }) + t('tl.keyHoldDelete')}
                  style={{
                    position: 'absolute', top: '50%', left: k.time * pps - 5,
                    width: 10, height: 10, marginTop: -5,
                    background: k.value === 'on' ? TRACK_COLORS[2] : 'transparent',
                    border: `1.5px solid ${TRACK_COLORS[2]}`, borderRadius: 2,
                    cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize', zIndex: 2, touchAction: 'none',
                  }}
                />
              ))}
              {/* MARK lane — who this player is riding an opponent at. The
                  markup is shared with the pinned block (markLane), so a mark
                  reads identically wherever it is drawn. */}
              {ti === 3 && markLane(trackId, markKeys)}
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  });

  // ── Pinned tracks block — selected item (ball/cone/target/zone) OR selected
  // player. Renders ABOVE the player blocks, fixed at the top of the strip, and
  // looks exactly like a normal block. Items swap the Action track for an
  // Attachment track when attachable; zones use a Visual third lane; players
  // show their own Movement/Action/Graphic tracks.
  const itemBlock = (() => {
    const sel = selectedItem;
    if (!sel) return null;

    // Common per-block variables, filled by the player or item branch below.
    let isBall = false, isZone = false, attachable = false;
    let trackId, track, posKeys = [], midKeys = [], graphicKeys = [], visualKeys = [], markKeys = [];
    let label = '', tagText = 'ITEM', sideLetter = null, badgeStyle = {}, badgeContent = null;
    let midName = 'Action', midColor = TRACK_COLORS[1], midIcon = TRACK_ICONS['Action'];
    let thirdName = 'Graphic', thirdIcon = TRACK_ICONS['Graphic'];
    let onPosKey = null, onMidKey = null, onThirdKey = null, onMarkKey = null, onAttachKey = null;
    // A player's four lanes are Movement · Action · Graphic · MARK. The pinned
    // block used to show three of them, which made it a three-quarter view of
    // the player and left the lane people came to the strip for down in the
    // squad list, below the fold.
    let showMark = false;
    // A cone or target that is CARRIED gets a 4th lane of its own (Attachment),
    // so its follow keys are readable and editable here like every other lane.
    let attachKeys = [], showAttachLane = false;

    if (sel.type === 'player') {
      // ── Selected player: pin its Movement/Action/Graphic tracks at the top ──
      const p = players.find((pl) => pl.side === sel.side && pl.index === sel.index);
      if (!p) return null;
      trackId = `${p.side}:${p.index}`;
      track = keyframes.find((k) => k.id === trackId);
      posKeys = track?.position || [];
      midKeys = track?.action || [];
      graphicKeys = track?.graphic || [];
      label = p.name || `Player ${p.index + 1}`;
      tagText = 'PLAYER';
      sideLetter = p.side === 'home' ? 'H' : 'A';
      badgeStyle = {
        background: p.color || (p.side === 'home' ? '#2563eb' : '#dc2626'),
        color: '#fff', fontSize: '9px', fontWeight: '800',
      };
      badgeContent = p.number || p.index + 1;
      onPosKey = () => onPlayerKey && onPlayerKey(p.side, p.index);
      onMidKey = () => onActionPrompt && onActionPrompt({ side: p.side, index: p.index });
      onThirdKey = () => onGraphicFlip && onGraphicFlip({ side: p.side, index: p.index });
      // The pinned block is the row the coach is actually working on, so it
      // carries the MARK lane too: the ✚ opens the same compass, the diamonds
      // are the same markup, and nothing about the player depends on how far
      // the strip happens to be scrolled.
      markKeys = track?.attach || [];
      onMarkKey = () => onMarkPrompt && onMarkPrompt(trackId);
      showMark = true;
    } else {
      // ── Selected item (ball / cone / target / zone) ──
      isBall = sel.type === 'ball';
      isZone = sel.type === 'zone';
      attachable = isBall || isZone; // ball & zone tracks support attachment keys
      const itemType = ['ball', 'cone', 'target', 'zone'].includes(sel.type) ? sel.type : null;
      if (!itemType) return null;
      trackId = `${itemType}:${sel.id ?? ''}`;
      track = keyframes.find((k) => k.id === trackId);
      posKeys = track?.position || [];
      midKeys = attachable ? (track?.attach || []) : (track?.action || []);
      // A cone or a target rides a carrier too — the pitch drop and the item
      // menu both write that key. Until now the strip could not SHOW it: the
      // follow was invisible while editing, unreachable for a scrub, a re-pick
      // or a hold-to-delete. The 4th lane appears only when the item is
      // carried, so a cone standing on its own keeps exactly the three lanes it
      // has always had.
      attachKeys = attachable ? [] : (track?.attach || []);
      showAttachLane = attachKeys.length > 0;
      graphicKeys = track?.graphic || [];
      visualKeys = track?.visual || []; // zones: radius / shape / vertex windows
      label = isBall ? 'Ball' : itemType.charAt(0).toUpperCase() + itemType.slice(1);
      const itemIcon = ITEM_ICONS[itemType];
      badgeStyle = {
        background: isBall ? '#e2e8f0' : 'rgba(167,139,250,0.35)',
        color: isBall ? '#0f172a' : '#a78bfa',
      };
      badgeContent = itemIcon;
      midName = attachable ? 'Attachment' : 'Action';
      midColor = attachable ? ATTACH_COLOR : TRACK_COLORS[1];
      midIcon = attachable ? ICON_LINK : TRACK_ICONS['Action'];
      thirdName = isZone ? 'Visual' : 'Graphic';
      thirdIcon = isZone ? TRACK_ICONS['Visual'] : TRACK_ICONS['Graphic'];
      onPosKey = () => onItemPositionKey && onItemPositionKey(trackId);
      onMidKey = () => attachable
        ? onBallAttachPrompt && onBallAttachPrompt(trackId)
        : onActionPrompt && onActionPrompt({ trackId });
      // The 4th slot for a carried cone or target opens the SAME attach prompt
      // a ball's middle lane opens — one door, one prompt, whatever the shape.
      onAttachKey = () => onBallAttachPrompt && onBallAttachPrompt(trackId);
      onThirdKey = () => isZone
        ? onZoneVisualKey && onZoneVisualKey(trackId)
        : onGraphicFlip && onGraphicFlip({ trackId });
    }

    // attachLabel — hoisted to the component body (see below the locked-key
    // helpers): both the item row and the player row's MARK lane name a carrier.

    return (
      <div style={{
        display: 'flex', height: (showMark || showAttachLane ? 4 : 3) * LANE_H, flexShrink: 0,
        borderBottom: '1px solid rgba(167,139,250,0.45)',
        background: 'rgba(167,139,250,0.07)',
      }}>
        {/* Name cell — icon/number + label + 3 key buttons */}
        <div style={{
          width: NAME_W, flexShrink: 0, background: 'rgba(30,27,75,0.6)',
          borderRight: '1px solid #334155',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          gap: '3px', padding: '0 6px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '3px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...badgeStyle,
            }}>{badgeContent}</span>
            <span style={{ color: '#e2e8f0', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
              {label}
            </span>
            {sideLetter && (
              <span style={{ color: '#64748b', fontSize: '9px', fontWeight: '600', flexShrink: 0 }}>
                {sideLetter}
              </span>
            )}
            <span style={{ color: '#a78bfa', fontSize: '9px', fontWeight: '800', flexShrink: 0 }}>{tagText}</span>
          </div>
          {/* Key buttons — same 3 slots as a player (key glyph + track icon) */}
          <div className="v3-keyrow" style={{ display: 'flex', gap: '2px', position: 'relative' }}>
            <button
              type="button"
              onClick={onPosKey}
              title={t('tl.keyMovement')}
              style={{ ...keyBtnStyle, color: TRACK_COLORS[0] }}
            >{KEY_ICON}{TRACK_ICONS['Movement']}</button>
            <button
              type="button"
              onClick={onMidKey}
              title={attachable ? t('tl.keyAttachment') : t('tl.keyAction')}
              style={{ ...keyBtnStyle, color: '#e2e8f0' }}
            >{KEY_ICON}{midIcon}</button>
            <button
              type="button"
              onClick={onThirdKey}
              title={isZone ? t('tl.keyVisual') : t('tl.keyGraphic')}
              style={{ ...keyBtnStyle, color: '#e2e8f0' }}
            >{KEY_ICON}{thirdIcon}</button>
            {/* ✚ MARK — the 4th slot, same compass, same colour, same glyph as
                the squad row's. The pinned block is where a selected player is
                worked on, so the mark has to be reachable from here. */}
            {showMark && (
              <button
                type="button"
                onClick={onMarkKey}
                title={t('tl.keyMark')}
                style={{ ...keyBtnStyle, color: TRACK_COLORS[3] }}
              >{KEY_ICON}{TRACK_ICONS['Mark']}</button>
            )}
            {/* ✚ ATTACHMENT — the 4th slot for a carried cone or target, the
                same link glyph and rose the ball's and zone's lane wears. */}
            {showAttachLane && (
              <button
                type="button"
                onClick={onAttachKey}
                title={t('tl.keyAttachment')}
                style={{ ...keyBtnStyle, color: ATTACH_COLOR }}
              >{KEY_ICON}{ICON_LINK}</button>
            )}
          </div>
        </div>

        {/* The 3 track lanes (Movement / Attachment-or-Action / Graphic-or-Visual) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          {(() => {
            // Every lane answers the hold. The zone's VISUAL lane ✚ starts a
            // shape WINDOW rather than keying a pose, and holding the lane
            // does the same thing at the instant the finger landed — a lane
            // that answers three holds and not the fourth is a rule with a
            // door left open.
            const itemLaneTypes = ['position', attachable ? 'attach' : 'action', isZone ? 'visual' : 'graphic'];
            const lanes = [
              ['Movement', TRACK_COLORS[0], TRACK_ICONS['Movement']],
              [midName, midColor, midIcon],
              [thirdName, isZone ? '#a78bfa' : TRACK_COLORS[2], thirdIcon],
            ];
            // A player's pinned block is four lanes, exactly like their row in
            // the squad list — same names, same colours, same glyphs, same
            // hold. An item keeps its three.
            if (showMark) {
                lanes.push(['Mark', TRACK_COLORS[3], TRACK_ICONS['Mark']]);
                itemLaneTypes.push('attach');
            } else if (showAttachLane) {
                // The 4th lane for a carried cone or target. The Action lane
                // stays where it was — a cone that follows someone still has
                // actions, and taking its lane away would be a layout that
                // lies about what the item can do.
                lanes.push(['Attachment', ATTACH_COLOR, ICON_LINK]);
                itemLaneTypes.push('attach');
            }
            return (
          lanes.map(([tn, col, ico], ti) => (
            <div key={tn} style={{
              height: LANE_H, borderBottom: ti < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              display: 'flex', alignItems: 'center',
            }}>
              <span title={laneName(tn) + t('tl.laneHold')} style={{
                width: ICON_W, flexShrink: 0, color: col,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                userSelect: 'none',
              }}>{ico}</span>
              <div
                data-testid="lane-area"
                onPointerDown={(e) => startLaneHold(e, trackId, itemLaneTypes[ti])}
                style={{ flex: 1, height: '100%', minWidth: 0, position: 'relative' }}
              >
                {ti === 0 && segmentBars(track, trackId)}
                {ti === 0 && posKeys.map(k => (
                  <Fragment key={`ip${k.time}`}>
                    <div
                      {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'position', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'position', k.time))}
                      title={isLockedKey(k.time) ? t('branch.lockedKey') : t('tl.posKey', { t: fmtTime(k.time) }) + t('tl.keyHoldDelete')}
                      style={{ position: 'absolute', top: '50%', left: k.time * pps - 4, width: 8, height: 8, marginTop: -4, background: TRACK_COLORS[0], border: '1px solid #0b1120', borderRadius: 1, cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize', zIndex: 2, touchAction: 'none' }}
                    />
                    {lockBadge(k.time)}
                  </Fragment>
                ))}
                {ti === 1 && attachable && attachLane(trackId, midKeys)}
                {ti === 1 && !attachable && midKeys.map(k => (
                  <div
                    key={`im${k.time}`}
                    {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'action', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'action', k.time))}
                    title={isLockedKey(k.time) ? t('branch.lockedKey')
                      : (t('tl.midAction', { what: ACTION_TYPES.find(a => a.value === k.value)?.label || k.value, t: fmtTime(k.time) }) + t('tl.keyHoldDelete'))}
                    style={{ position: 'absolute', top: '50%', left: k.time * pps - 4, width: 8, height: 8, marginTop: -4, background: midColor, border: '1px solid #0b1120', borderRadius: 1, cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize', zIndex: 2, touchAction: 'none' }}
                  />
                ))}
                {ti === 2 && !isZone && graphicBars(track, trackId)}
                {ti === 2 && !isZone && graphicKeys.map(k => (
                  <div
                    key={`ig${k.time}`}
                    {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'graphic', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'graphic', k.time), 'square')}
                    title={isLockedKey(k.time) ? t('branch.lockedKey') : t('tl.graphicKey', { value: k.value === 'on' ? t('tl.on') : t('tl.off'), t: fmtTime(k.time) }) + t('tl.keyHoldDelete')}
                    style={{ position: 'absolute', top: '50%', left: k.time * pps - 5, width: 10, height: 10, marginTop: -5, background: k.value === 'on' ? TRACK_COLORS[2] : 'transparent', border: `1.5px solid ${TRACK_COLORS[2]}`, borderRadius: 2, cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize', zIndex: 2, touchAction: 'none' }}
                  />
                ))}
                {ti === 2 && isZone && visualBars(track, trackId)}
                {ti === 2 && isZone && visualKeys.map(k => (
                  <div
                    key={`iv${k.time}`}
                    {...keyHandlers(k.time, (e) => startKeyDrag(e, trackId, 'visual', k.time), () => onKeyframeDelete && onKeyframeDelete(trackId, 'visual', k.time))}
                    title={isLockedKey(k.time) ? t('branch.lockedKey') : t('tl.visualKey', { t: fmtTime(k.time) }) + t('tl.keyHoldDelete')}
                    style={{ position: 'absolute', top: '50%', left: k.time * pps - 4, width: 8, height: 8, marginTop: -4, background: '#a78bfa', border: '1px solid #0b1120', borderRadius: 1, cursor: isLockedKey(k.time) ? 'not-allowed' : 'ew-resize', zIndex: 2, touchAction: 'none' }}
                  />
                ))}
                {/* MARK lane — the same markup the squad row draws, so a mark
                    looks the same wherever it is read from. On an item the 4th
                    lane is the ATTACHMENT lane instead, drawn by the same
                    helper the ball's and zone's middle lane uses. */}
                {ti === 3 && (showMark ? markLane(trackId, markKeys) : attachLane(trackId, attachKeys))}
              </div>
            </div>
          ))
            );
          })()}
        </div>
      </div>
    );
  })();


  const playheadX = TIME_X + playheadTime * pps;

  // ── BRANCH EDGE — drag the right edge of the ruler to stretch/shorten
  //    THIS BRANCH. Timing control without touching the number field: grab
  //    the edge, pull, watch the ticks rescale, release. Snaps to the ¼s
  //    key grid, NEVER shortens past the last key (a drag must not orphan
  //    dots it just hid), capped at 600 s like the field.
  let lastKeyT = 0;
  for (const tr of keyframes) {
    for (const lane of ['position', 'action', 'attach', 'graphic', 'visual']) {
      for (const k of (tr[lane] || [])) if (k.time > lastKeyT) lastKeyT = k.time;
    }
  }
  const startDurationDrag = useCallback((e) => {
    if (!onDurationChange) return;
    e.stopPropagation(); // the ruler scrubs on pointerdown — this grabs TIME, not the playhead
    e.preventDefault();
    const d0 = duration;
    const pps0 = pps; // FROZEN at grab: pps rescales as duration changes, so
    // mapping the pointer through the LIVE pps would feedback-loop the drag.
    const minD = Math.max(1, lastKeyT);
    const x0 = e.clientX;
    let last = d0;
    const move = (ev) => {
      const t = snapKeyTime(d0 + (ev.clientX - x0) / pps0);
      const clamped = Math.min(600, Math.max(minD, t));
      if (clamped !== last) { last = clamped; onDurationChange(clamped); }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (last !== d0 && onDurationDragEnd) onDurationDragEnd(last, d0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [duration, pps, lastKeyT, onDurationChange, onDurationDragEnd]);

  /** Commit the edited duration (Enter / blur). Validates + clamps to [1, 600]. */
  const commitDuration = () => {
    const v = Math.round(Number(durText));
    if (!Number.isFinite(v) || v < 1) { setDurText(String(duration)); return; }
    const clamped = Math.min(v, 600);
    if (clamped !== duration && onDurationChange) onDurationChange(clamped);
    setDurText(String(clamped));
  };

  return (
    <>
      {/* ═══ MASTER TIMELINE — overlaps the bottom of the 3D viewport (higher z-index) ═══ */}
      <div
        ref={rulerRef}
        data-testid="timeline-ruler"
        className="v3-ruler"
        title={t('tl.scrubHint')}
        onPointerDown={startScrub}
        style={{
          position: 'absolute', bottom: stripHeight, left: 0, right: 0, height: 30,
          zIndex: 55, background: 'rgba(2,6,23,0.96)', borderTop: '1px solid #334155',
          borderBottom: '1px solid #334155', cursor: 'crosshair', overflow: 'hidden',
          touchAction: 'none', userSelect: 'none',
        }}
      >
        {/* Name + icon spacer zones (align ruler with the lanes below).
            V3: the empty space above the AUTO button holds the BACK button that
            leaves this branch's timeline for the scenario tree. */}
        <div style={{ width: NAME_W, flexShrink: 0, borderRight: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 6, padding: '0 6px', overflow: 'hidden', boxSizing: 'border-box' }}>
          {onBranchBack && (
            <>
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onBranchBack(); }}
                title={t('branch.backTitle')}
                style={{
                  flexShrink: 0, height: 22, padding: '0 8px', borderRadius: 5,
                  cursor: 'pointer', background: 'rgba(99,102,241,0.22)', color: '#c7d2fe',
                  border: '1px solid #6366f1', fontSize: 10.5, fontWeight: 800,
                  letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <span style={{ fontSize: 12, lineHeight: 1 }}>‹</span> {t('branch.back')}
              </button>
              {branchName && !renaming && (
                <span
                  title={t('tl.branchRename', { name: branchName })}
                  onPointerDown={startNameHold}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (!onRenameBranch || !branchId) return;
                    setRenameDraft(branchName);
                    setRenaming(true);
                  }}
                  style={{
                    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', color: '#cbd5e1', fontSize: 11, fontWeight: 700,
                  }}
                >
                  {branchName}
                </span>
              )}
              {branchName && renaming && (
                <input
                  autoFocus
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onBlur={() => setRenaming(false)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') { onRenameBranch && onRenameBranch(branchId, renameDraft); setRenaming(false); }
                    else if (e.key === 'Escape') setRenaming(false);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label={t('tl.branchName')}
                  style={{
                    width: 130, minWidth: 0, fontSize: 11, fontWeight: 700, color: '#f8fafc',
                    background: '#1e293b', border: '1px solid #6366f1', borderRadius: 4, padding: '2px 6px',
                  }}
                />
              )}
            </>
          )}
        </div>
        <div style={{ width: ICON_W, flexShrink: 0, borderRight: '1px solid rgba(51,65,85,0.5)' }} />
        {ticks}
        {/* Branch edge grab: stretch/shorten TIME itself (see startDurationDrag). */}
        <div
          data-testid="duration-edge"
          className="v3-edge"
          onPointerDown={startDurationDrag}
          title={t('tl.stretchHint')}
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: 12,
            cursor: 'ew-resize', zIndex: 20, touchAction: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            background: 'linear-gradient(to right, transparent, rgba(148,163,184,0.16))',
          }}
        >
          <div style={{ width: 2, height: '60%', background: '#94a3b8', borderRadius: 1, opacity: 0.85 }} />
        </div>
        {/* Playhead — red line + top handle + time bubble */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: playheadX, width: 2, background: '#ef4444', zIndex: 10, pointerEvents: 'none' }}>
          <div style={{
            position: 'absolute', top: 0, left: -7, width: 0, height: 0,
            borderLeft: '7px solid transparent', borderRight: '7px solid transparent',
            borderTop: '9px solid #ef4444',
          }} />
        </div>
        <div style={{
          position: 'absolute', top: 3, left: playheadX + 8, zIndex: 11,
          background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '800',
          borderRadius: '3px', padding: '1px 5px', pointerEvents: 'none', whiteSpace: 'nowrap',
          boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
        }}>
          {fmtTime(playheadTime)}
          {onSpeedCycle ? (
            <span
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onSpeedCycle(); }}
              title={t('tl.speedChipTitle')}
              style={{
                pointerEvents: 'auto', cursor: 'pointer', marginLeft: 4,
                color: speed === 1 ? 'rgba(255,255,255,0.65)' : '#fff',
                textDecoration: 'underline', textUnderlineOffset: 2,
              }}
            >{speed}×</span>
          ) : (speed !== 1 ? ` · ${speed}×` : '')}
        </div>
      </div>

      {/* ═══ BOTTOM STRIP — player track lanes ═══ */}
      <div className="v2-strip" style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: stripHeight,
        background: '#0b1120', borderTop: '1px solid #334155', zIndex: 40,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Resize handle */}
        <div onPointerDown={startResize}
          title={t('tl.resizeHandle')}
          style={{
            position: 'absolute', top: -5, left: 0, right: 0, height: 10,
            cursor: 'row-resize', touchAction: 'none', zIndex: 60, display: 'flex', alignItems: 'center',
            justifyContent: 'center',
          }}>
          <div style={{ width: '60px', height: '4px', borderRadius: '2px', background: '#475569', opacity: 0.9 }} />
        </div>

        {/* Header row — AUTO toggle on the left */}
        <div className="v2-strip-header" style={{
          height: 22, flexShrink: 0, display: 'flex', alignItems: 'center',
          padding: '0 10px', gap: '8px', background: 'rgba(15,23,42,0.7)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          {/* AUTO — toggle auto-keying of moved items */}
          <button
            type="button"
            onClick={onAutoKeyToggle}
            title={t('tl.autoKeyHint')}
            style={{
              background: autoKeyOn ? 'rgba(56,189,248,0.95)' : 'rgba(30,41,59,0.9)',
              color: autoKeyOn ? '#082f49' : '#64748b', border: '1px solid #334155',
              borderRadius: '3px', padding: '1px 10px', cursor: 'pointer',
              fontSize: '10px', fontWeight: '800', letterSpacing: '0.06em',
            }}
          >
            AUTO {autoKeyOn ? 'ON' : 'OFF'}
          </button>
          <span style={{ color: '#334155', fontSize: '10px' }}>|</span>
          <span style={{ color: '#64748b', fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em' }}>{t('tl.timeline')}</span>
          <span style={{ color: '#334155', fontSize: '10px' }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={durText}
              onChange={(e) => setDurText(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={commitDuration}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { commitDuration(); e.currentTarget.blur(); }
                if (e.key === 'Escape') { setDurText(String(duration)); e.currentTarget.blur(); }
              }}
              title={t('tl.durationHint')}
              style={{
                width: '34px', background: 'rgba(148,163,184,0.08)',
                border: '1px solid rgba(148,163,184,0.35)', borderRadius: '3px',
                color: '#94a3b8', fontSize: '10px', fontWeight: '700',
                padding: '1px 3px', textAlign: 'center', outline: 'none',
              }}
            />
            <span style={{ color: '#64748b', fontSize: '10px' }}>s</span>
          </span>
          <span style={{ color: '#334155', fontSize: '10px' }}>|</span>
          <span className="v2-strip-hint" style={{ color: '#475569', fontSize: '10px' }}>{t('tl.hint')}</span>
          <button
            type="button"
            onClick={(e) => onPlayToggle && onPlayToggle(e)}
            aria-label={isPlaying ? t('tl.pauseBranch') : t('tl.playBranch')}
            title={isPlaying ? t('tl.pauseBranchHint') : t('tl.playBranchHint')}
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
              color: isPlaying ? '#fca5a5' : '#ef4444', fontSize: '11px', fontWeight: '800',
              background: isPlaying ? 'rgba(239,68,68,0.28)' : 'rgba(239,68,68,0.12)',
              borderRadius: '4px', padding: '2px 8px',
              border: '1px solid rgba(239,68,68,0.35)', cursor: 'pointer',
            }}
          >
            {isPlaying ? (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                <rect x="5" y="4" width="5" height="16" rx="1" /><rect x="14" y="4" width="5" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 4l13 8-13 8z" />
              </svg>
            )}
            {fmtTime(playheadTime)} / {fmtTime(duration)}
          </button>
        </div>

        {/* Vertical grid lines (fit-to-width) — shared by pinned + scroll areas */}
        {(() => {
          const gridLines = Array.from({ length: Math.floor(duration) + 1 }).map((_, s) => (
            <div key={`g${s}`} style={{
              position: 'absolute', top: 0, bottom: 0, left: TIME_X + s * pps, width: 1,
              background: s % 5 === 0 ? 'rgba(148,163,184,0.14)' : 'rgba(148,163,184,0.05)',
              pointerEvents: 'none',
            }} />
          ));
          const playheadLine = (
            <div style={{
              position: 'absolute', top: 0, bottom: 0, left: playheadX,
              width: 2, background: 'rgba(239,68,68,0.85)', zIndex: 20, pointerEvents: 'none',
            }} />
          );
          return (
            <>
              {/* Pinned item tracks (selected item) — always visible ABOVE the
                  scrollable player list; players scroll underneath them */}
              {itemBlock && (
                <div style={{ position: 'relative', flexShrink: 0, overflow: 'hidden' }}>
                  <div style={{ position: 'relative' }}>
                    {gridLines}
                    {itemBlock}
                    {playheadLine}
                  </div>
                </div>
              )}
              {/* Tracks — vertical scroll only (players); the playhead is the time scroller */}
              <div ref={lanesRef}
                className="v2-lanes"
                style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative', minHeight: 0 }}>
                <div style={{ position: 'relative', minHeight: '100%' }}>
                  {gridLines}
                  {blocks}
                  {players.length === 0 && !selectedItem && (
                    <div style={{ color: '#475569', fontSize: '11px', padding: '10px', lineHeight: 1.5 }}>
                      {t('tl.noPlayers')}
                      <br />{t('tl.addPlayersHint')}
                    </div>
                  )}
                  {/* Playhead line — spans all players, moves with time (independent of scroll) */}
                  {playheadLine}
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </>
  );
}

// ── Shared inline style for the per-player key buttons ──
const keyBtnStyle = {
  flex: 1, minWidth: 0, background: 'rgba(30,41,59,0.9)', color: '#e2e8f0',
  border: '1px solid #334155', borderRadius: '3px', padding: '2px 2px',
  cursor: 'pointer', fontSize: '7.5px', fontWeight: '700', whiteSpace: 'nowrap',
  overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.2',
};
